import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { Combobox } from '@/components/ui/combobox';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { TabBar } from '@/components/ui/tab-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { RestaurantSection, RestaurantTable } from '@/types/restaurant';

const TABLE_SHAPES= ['SQUARE', 'ROUND', 'RECTANGLE'];

const emptySection = { name: '', color: '#6366f1', sortOrder: 0, locationId: '' };
const emptyTable = { tableNo: '', capacity: 4, sectionId: '', shape: 'SQUARE', posX: 0, posY: 0, locationId: '' };
const emptyStation = { name: '', locationId: '', alertAfterMinutes: 10 };

const TABS = ['Sections', 'Tables', 'KDS Stations'] as const;
type Tab = typeof TABS[number];

interface SectionPayload {
  name: string;
  color: string;
  sortOrder: number;
  locationId?: string;
}

interface TablePayload {
  tableNo: string;
  capacity: number;
  sectionId?: string;
  shape: string;
  posX: number;
  posY: number;
  locationId?: string;
}

interface StationPayload {
  name: string;
  locationId?: string;
  alertAfterMinutes: number;
}

interface KdsStation { id: string; name: string; locationId?: string; alertAfterMinutes: number; }

export function RestaurantSetupPage() {
  const [activeTab, setActiveTab] = useState<Tab>('Sections');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string; type: string } | null>(null);

  // Section state
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<RestaurantSection | null>(null);
  const [sectionForm, setSectionForm] = useState(emptySection);

  // Table state
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null);
  const [tableForm, setTableForm] = useState(emptyTable);

  // Station state
  const [stationDialogOpen, setStationDialogOpen] = useState(false);
  const [editingStation, setEditingStation] = useState<KdsStation | null>(null);
  const [stationForm, setStationForm] = useState(emptyStation);

  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('restaurant');
  const canWrite = hasPermission('restaurant:write') || hasPermission('restaurant:*');

  // Sections
  const { data: sections } = useQuery<RestaurantSection[]>({
    queryKey: ['restaurant-sections'],
    queryFn: () => api.get('/restaurant/sections').then((r) => r.data?.data ?? r.data ?? []),
  });

  // Tables
  const { data: tables } = useQuery<RestaurantTable[]>({
    queryKey: ['restaurant-tables'],
    queryFn: () => api.get('/restaurant/tables').then((r) => r.data?.data ?? r.data ?? []),
  });

  // KDS Stations
  const { data: stations } = useQuery<KdsStation[]>({
    queryKey: ['kds-stations'],
    queryFn: () => api.get('/kitchen/stations').then((r) => r.data?.data ?? r.data ?? []),
  });

  // Locations
  const { data: locations } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['locations'],
    queryFn: () => api.get('/locations').then((r) => r.data?.data ?? r.data ?? []),
  });

  const saveSectionMutation = useMutation({
    mutationFn: (data: SectionPayload) =>
      editingSection
        ? api.patch(`/restaurant/sections/${editingSection.id}`, data)
        : api.post('/restaurant/sections', data),
    onSuccess: () => {
      toast.success(editingSection ? t('sectionUpdated') : t('sectionCreated'));
      queryClient.invalidateQueries({ queryKey: ['restaurant-sections'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      setSectionDialogOpen(false);
      setEditingSection(null);
      setSectionForm(emptySection);
    },
    onError: () => toast.error(t('failedToSaveSection')),
  });

  const saveTableMutation = useMutation({
    mutationFn: (data: TablePayload) =>
      editingTable
        ? api.patch(`/restaurant/tables/${editingTable.id}`, data)
        : api.post('/restaurant/tables', data),
    onSuccess: () => {
      toast.success(editingTable ? t('tableUpdated') : t('tableCreated'));
      queryClient.invalidateQueries({ queryKey: ['restaurant-tables'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      setTableDialogOpen(false);
      setEditingTable(null);
      setTableForm(emptyTable);
    },
    onError: () => toast.error(t('failedToSaveTable')),
  });

  const saveStationMutation = useMutation({
    mutationFn: (data: StationPayload) =>
      editingStation
        ? api.patch(`/kitchen/stations/${editingStation.id}`, data)
        : api.post('/kitchen/stations', data),
    onSuccess: () => {
      toast.success(editingStation ? t('stationUpdated') : t('stationCreated'));
      queryClient.invalidateQueries({ queryKey: ['kds-stations'] });
      setStationDialogOpen(false);
      setEditingStation(null);
      setStationForm(emptyStation);
    },
    onError: () => toast.error(t('failedToSaveStation')),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: string }) => {
      if (type === 'section') return api.delete(`/restaurant/sections/${id}`);
      if (type === 'table') return api.delete(`/restaurant/tables/${id}`);
      return api.delete(`/kitchen/stations/${id}`);
    },
    onSuccess: (_, { type }) => {
      if (type === 'section') toast.success(t('sectionDeleted'));
      else if (type === 'table') toast.success(t('tableDeleted'));
      else toast.success(t('stationDeleted'));
      queryClient.invalidateQueries({ queryKey: ['restaurant-sections'] });
      queryClient.invalidateQueries({ queryKey: ['restaurant-tables'] });
      queryClient.invalidateQueries({ queryKey: ['kds-stations'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      setDeleteTarget(null);
    },
    onError: () => toast.error(t('failedToDelete')),
  });

  return (
    <div className="space-y-6 pb-24 lg:pb-6 page-enter">
      <PageHeader
        title={t('restaurantSetup')}
        subtitle={t('setupSubtitle')}
      />

      <TabBar
        tabs={TABS.map((tab) => ({
          key: tab,
          label: tab === 'Sections' ? t('sections') : tab === 'Tables' ? t('tables') : t('kdsStations'),
        }))}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as Tab)}
      />

      {/* Sections tab */}
      {activeTab === 'Sections' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t('sections')}</CardTitle>
              {canWrite && (
                <Button size="sm" onClick={() => { setEditingSection(null); setSectionForm(emptySection); setSectionDialogOpen(true); }}>
                  <Plus className="me-1.5 h-4 w-4" />{t('addSection')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {(sections ?? []).map((sec) => (
                <div key={sec.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <span className="h-4 w-4 rounded-full flex-shrink-0" style={{ backgroundColor: sec.color }} />
                    <span className="font-medium">{sec.name}</span>
                    <span className="text-xs text-muted-foreground">{t('tablesCount', { count: sec._count?.tables ?? 0 })}</span>
                  </div>
                  {canWrite && (
                    <TableRowActions
                      actions={[
                        {
                          key: 'edit',
                          label: t('editSection'),
                          icon: Pencil,
                          onSelect: () => { setEditingSection(sec); setSectionForm({ name: sec.name, color: sec.color, sortOrder: sec.sortOrder, locationId: sec.locationId ?? '' }); setSectionDialogOpen(true); },
                          primary: true,
                        },
                        {
                          key: 'delete',
                          label: t('deleteSection', { defaultValue: 'Delete' }),
                          icon: Trash2,
                          onSelect: () => setDeleteTarget({ id: sec.id, label: sec.name, type: 'section' }),
                          variant: 'destructive',
                        },
                      ]}
                    />
                  )}
                </div>
              ))}
              {(sections ?? []).length === 0 && (
                <p className="py-8 text-center text-muted-foreground text-sm">{t('noSectionsYet')}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tables tab */}
      {activeTab === 'Tables' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t('tables')}</CardTitle>
              {canWrite && (
                <Button size="sm" onClick={() => { setEditingTable(null); setTableForm(emptyTable); setTableDialogOpen(true); }}>
                  <Plus className="me-1.5 h-4 w-4" />{t('addTable')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Mobile: card view */}
            <div className="sm:hidden space-y-3">
              {(tables ?? []).map((tbl) => (
                <div key={tbl.id} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold">#{tbl.tableNo}</span>
                    <span className="text-xs text-muted-foreground">{t(tbl.status === 'AVAILABLE' ? 'available' : tbl.status === 'OCCUPIED' ? 'occupied' : tbl.status === 'RESERVED' ? 'reserved' : tbl.status === 'CLEANING' ? 'cleaning' : 'tableStatus')}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span>{tbl.section?.name ?? t('unassigned')}</span>
                    <span>{tbl.capacity} {t('seatsLabel')}</span>
                    <span className="capitalize">{tbl.shape.toLowerCase()}</span>
                  </div>
                  {canWrite && (
                    <TableRowActions
                      actions={[
                        {
                          key: 'edit',
                          label: t('editTable', { defaultValue: 'Edit' }),
                          icon: Pencil,
                          onSelect: () => {
                            setEditingTable(tbl);
                            setTableForm({ tableNo: tbl.tableNo, capacity: tbl.capacity, sectionId: tbl.sectionId ?? '', shape: tbl.shape, posX: tbl.posX, posY: tbl.posY, locationId: tbl.locationId ?? '' });
                            setTableDialogOpen(true);
                          },
                          primary: true,
                        },
                        {
                          key: 'delete',
                          label: t('deleteTable', { defaultValue: 'Delete' }),
                          icon: Trash2,
                          onSelect: () => setDeleteTarget({ id: tbl.id, label: t('tableNo', { no: tbl.tableNo }), type: 'table' }),
                          variant: 'destructive',
                        },
                      ]}
                    />
                  )}
                </div>
              ))}
              {(tables ?? []).length === 0 && (
                <p className="py-8 text-center text-muted-foreground text-sm">{t('noTablesYet')}</p>
              )}
            </div>
            {/* Desktop: table view */}
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('tableNumber')}</Th>
                    <Th>{t('sectionHeader')}</Th>
                    <Th>{t('capacity')}</Th>
                    <Th>{t('shape')}</Th>
                    <Th>{t('status')}</Th>
                    {canWrite && <Th>{t('actions')}</Th>}
                  </Tr>
                </THead>
                <TBody>
                  {(tables ?? []).map((tbl) => (
                    <Tr key={tbl.id}>
                      <Td label={t('tableNumber')} className="font-bold">#{tbl.tableNo}</Td>
                      <Td label={t('sectionHeader')} className="text-muted-foreground">{tbl.section?.name ?? '—'}</Td>
                      <Td label={t('capacity')}>{tbl.capacity} {t('seatsLabel')}</Td>
                      <Td label={t('shape')} className="capitalize">{tbl.shape.toLowerCase()}</Td>
                      <Td label={t('status')}>{t(tbl.status === 'AVAILABLE' ? 'available' : tbl.status === 'OCCUPIED' ? 'occupied' : tbl.status === 'RESERVED' ? 'reserved' : tbl.status === 'CLEANING' ? 'cleaning' : 'tableStatus')}</Td>
                      {canWrite && (
                        <Td label={t('actions')} className="text-end td-actions">
                          <TableRowActions
                            actions={[
                              {
                                key: 'edit',
                                label: t('editTable', { defaultValue: 'Edit' }),
                                icon: Pencil,
                                onSelect: () => {
                                  setEditingTable(tbl);
                                  setTableForm({ tableNo: tbl.tableNo, capacity: tbl.capacity, sectionId: tbl.sectionId ?? '', shape: tbl.shape, posX: tbl.posX, posY: tbl.posY, locationId: tbl.locationId ?? '' });
                                  setTableDialogOpen(true);
                                },
                                primary: true,
                              },
                              {
                                key: 'delete',
                                label: t('deleteTable', { defaultValue: 'Delete' }),
                                icon: Trash2,
                                onSelect: () => setDeleteTarget({ id: tbl.id, label: t('tableNo', { no: tbl.tableNo }), type: 'table' }),
                                variant: 'destructive',
                              },
                            ]}
                          />
                        </Td>
                      )}
                    </Tr>
                  ))}
                  {(tables ?? []).length === 0 && (
                    <Tr hover={false}><Td colSpan={6} className="py-8 text-center text-muted-foreground">{t('noTablesYet')}</Td></Tr>
                  )}
                </TBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KDS Stations tab */}
      {activeTab === 'KDS Stations' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t('kdsStations')}</CardTitle>
              {canWrite && (
                <Button size="sm" onClick={() => { setEditingStation(null); setStationForm(emptyStation); setStationDialogOpen(true); }}>
                  <Plus className="me-1.5 h-4 w-4" />{t('addStation')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {(stations ?? []).map((st) => (
                <div key={st.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium">{st.name}</p>
                    <p className="text-xs text-muted-foreground">{t('alertAfterMin', { min: st.alertAfterMinutes })}</p>
                  </div>
                  {canWrite && (
                    <TableRowActions
                      actions={[
                        {
                          key: 'edit',
                          label: t('editStation', { defaultValue: 'Edit' }),
                          icon: Pencil,
                          onSelect: () => { setEditingStation(st); setStationForm({ name: st.name, locationId: st.locationId ?? '', alertAfterMinutes: st.alertAfterMinutes }); setStationDialogOpen(true); },
                          primary: true,
                        },
                        {
                          key: 'delete',
                          label: t('deleteStation', { defaultValue: 'Delete' }),
                          icon: Trash2,
                          onSelect: () => setDeleteTarget({ id: st.id, label: st.name, type: 'station' }),
                          variant: 'destructive',
                        },
                      ]}
                    />
                  )}
                </div>
              ))}
              {(stations ?? []).length === 0 && (
                <p className="py-8 text-center text-muted-foreground text-sm">{t('noKdsStations')}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section dialog */}
      <Dialog
        visible={sectionDialogOpen}
        onHide={() => setSectionDialogOpen(false)}
        header={editingSection ? t('editSection') : t('addSection')}
        className="w-full max-w-lg"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">{t('nameLabel')} <span className="text-destructive-text">*</span></Label>
            <Input placeholder="e.g. Main Hall" value={sectionForm.name} onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })} />
          </div>
          <div className="flex items-center gap-4">
            <div>
              <Label className="mb-1.5 block">{t('color')}</Label>
              <input type="color" value={sectionForm.color} onChange={(e) => setSectionForm({ ...sectionForm, color: e.target.value })} className="h-9 w-20 cursor-pointer rounded border" />
            </div>
            <div className="flex-1">
              <Label className="mb-1.5 block">{t('sortOrderLabel')}</Label>
              <Input type="number" value={sectionForm.sortOrder} onChange={(e) => setSectionForm({ ...sectionForm, sortOrder: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t('locationOptional')}</Label>
            <Dropdown
              className="w-full"
              value={sectionForm.locationId}
              onChange={(e) => setSectionForm({ ...sectionForm, locationId: e.value })}
              options={[
                { label: t('noSpecificLocation'), value: '' },
                ...(locations ?? []).map((l) => ({ label: l.name, value: l.id })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('noSpecificLocation')}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setSectionDialogOpen(false)}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              onClick={() => {
                const { locationId, ...rest } = sectionForm;
                const payload: SectionPayload = locationId ? { ...rest, locationId } : rest;
                saveSectionMutation.mutate(payload);
              }}
              disabled={saveSectionMutation.isPending || !sectionForm.name}
            >
              {saveSectionMutation.isPending ? t('saving') : t('save')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Table dialog */}
      <Dialog
        visible={tableDialogOpen}
        onHide={() => setTableDialogOpen(false)}
        header={editingTable ? t('editTable') : t('addTable')}
        className="w-full max-w-lg"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">{t('tableNumber')} <span className="text-destructive-text">*</span></Label>
              <Input placeholder="e.g. 01" value={tableForm.tableNo} onChange={(e) => setTableForm({ ...tableForm, tableNo: e.target.value })} />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('capacitySeats')}</Label>
              <Input type="number" min="1" step="1" value={tableForm.capacity} onChange={(e) => setTableForm({ ...tableForm, capacity: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t('sectionHeader')}</Label>
            <Combobox
              options={[{ value: '', label: t('unassigned') }, ...(sections ?? []).map((s) => ({ value: s.id, label: s.name }))]}
              value={tableForm.sectionId}
              onChange={(val) => setTableForm({ ...tableForm, sectionId: val ?? '' })}
              placeholder={t('selectSectionPlaceholder')}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('locationOptional')}</Label>
            <Dropdown
              className="w-full"
              value={tableForm.locationId}
              onChange={(e) => setTableForm({ ...tableForm, locationId: e.value })}
              options={[
                { label: t('noSpecificLocation'), value: '' },
                ...(locations ?? []).map((l) => ({ label: l.name, value: l.id })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('noSpecificLocation')}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">{t('shape')}</Label>
              <Combobox
                options={TABLE_SHAPES.map((s) => ({ value: s, label: s.charAt(0) + s.slice(1).toLowerCase() }))}
                value={tableForm.shape}
                onChange={(val) => setTableForm({ ...tableForm, shape: val ?? 'SQUARE' })}
                placeholder="Select shape..."
              />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setTableDialogOpen(false)}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              onClick={() => {
                const { sectionId, locationId, ...rest } = tableForm;
                const payload: TablePayload = {
                  ...rest,
                  capacity: Number(tableForm.capacity),
                  ...(sectionId ? { sectionId } : {}),
                  ...(locationId ? { locationId } : {}),
                };
                saveTableMutation.mutate(payload);
              }}
              disabled={saveTableMutation.isPending || !tableForm.tableNo}
            >
              {saveTableMutation.isPending ? t('saving') : t('save')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Station dialog */}
      <Dialog
        visible={stationDialogOpen}
        onHide={() => setStationDialogOpen(false)}
        header={editingStation ? t('editKdsStation') : t('addKdsStation')}
        className="w-full max-w-lg"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">{t('stationNameLabel')} <span className="text-destructive-text">*</span></Label>
            <Input placeholder="e.g. Main Kitchen" value={stationForm.name} onChange={(e) => setStationForm({ ...stationForm, name: e.target.value })} />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('locationOptional')}</Label>
            <Dropdown
              className="w-full"
              value={stationForm.locationId}
              onChange={(e) => setStationForm({ ...stationForm, locationId: e.value })}
              options={[
                { label: t('noSpecificLocation'), value: '' },
                ...(locations ?? []).map((l) => ({ label: l.name, value: l.id })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('noSpecificLocation')}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('alertAfterMinutes')}</Label>
            <Input type="number" min="1" step="1" value={stationForm.alertAfterMinutes} onChange={(e) => setStationForm({ ...stationForm, alertAfterMinutes: Number(e.target.value) })} />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setStationDialogOpen(false)}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              onClick={() => {
                const { locationId, ...rest } = stationForm;
                const payload: StationPayload = {
                  ...rest,
                  alertAfterMinutes: Number(stationForm.alertAfterMinutes),
                  ...(locationId ? { locationId } : {}),
                };
                saveStationMutation.mutate(payload);
              }}
              disabled={saveStationMutation.isPending || !stationForm.name}
            >
              {saveStationMutation.isPending ? t('saving') : t('save')}
            </Button>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('delete')}
        message={`Are you sure you want to delete "${deleteTarget?.label}"?`}
        confirmLabel={t('delete')}
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate({ id: deleteTarget.id, type: deleteTarget.type })}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
